import { useRef, useState, type KeyboardEvent } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Cloud,
  Database,
  Layers3,
  Monitor,
  Plus,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Terminal,
  Workflow,
} from 'lucide-react';
import '../eldgard.css';
import { EldgardPricing } from '../components/EldgardPricing';
import { EldgardWaitlist } from '../components/EldgardWaitlist';

const contactHref = '#early-access';

const previews = [
  {
    id: 'overview',
    label: 'Inspect documents',
    image: 'bookings-table.webp',
    alt: 'Eldgard showing hotel booking documents with guest names, check-in dates, nights, payment status, stays, and totals in Norwegian kroner',
    caption:
      'Explore a bookings collection: guests, stays, payment status, and totals. Fictional demo records in the actual app.',
    width: 2400,
    height: 1500,
  },
  {
    id: 'import',
    label: 'Review a CSV import',
    image: 'csv-import.webp',
    alt: 'Actual Eldgard CSV import review showing two validated demo rows, explicit field types, and the create-new-documents-only policy',
    caption:
      'Map fields and types, validate every row, then review before importing. Existing IDs are not overwritten by CSV imports.',
    width: 1000,
    height: 800,
  },
  {
    id: 'connection',
    label: 'Connect your project',
    image: 'add-connection.png',
    alt: 'Eldgard Add connection dialog with Emulator, Service account, and Google options; the emulator form shows local host and port fields',
    caption:
      'Connect an emulator, sign in with Google, or use a service-account key on desktop.',
    width: 1920,
    height: 1200,
  },
] as const;

const mobilePreviews = [
  {
    id: 'home',
    title: 'Find the document.',
    image: 'mobile-home.png',
    alt: 'Eldgard mobile Home screen with cloud databases, local connections, saved queries, and query import shortcuts',
    caption:
      'Open an exact path, follow a reference, or return to a pinned place. Search the documents you have loaded without scanning the entire database.',
  },
  {
    id: 'query',
    title: 'Run a focused query.',
    image: 'mobile-query-builder.png',
    alt: 'Eldgard mobile query builder showing typed status and total filters with nested AND and OR conditions on a demo orders collection',
    caption:
      'Build collection or collection-group queries with typed AND/OR filters. Save queries, sort results, and request a server count. Reads start when you choose Run or Count.',
  },
  {
    id: 'document',
    title: 'Inspect without changing data.',
    image: 'mobile-document-fields.png',
    alt: 'Eldgard mobile document reader showing a fictional order with map, array, string, number, and boolean fields',
    caption:
      'Inspect typed fields and source JSON. Compare a document across refreshes or against a frozen baseline. The reader has no write operations; comparisons reflect your observations, not a live feed or complete change history.',
  },
] as const;

const capabilities = [
  {
    icon: Layers3,
    title: 'Answer the next question.',
    text: 'Query a collection or collection group with typed filters and sorting. Save the definition. Run server-side Count, Sum, or Average without fetching every matching document.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Edit the value. Keep its type.',
    text: 'Work with timestamps, references, geopoints, bytes, maps, and arrays—not just strings. Review a single-document change as a typed before-and-after diff before applying it.',
  },
  {
    icon: Database,
    title: 'Bring the right data along.',
    text: 'Map CSV columns to Firestore fields and validate the whole file before creating documents. Choose loaded results, a fresh query, or a whole collection for CSV export. Use typed JSON for richer data.',
  },
  {
    icon: Workflow,
    title: 'Reproduce it locally.',
    text: 'Copy a cloud collection and its subcollections into an empty emulator destination. Or use Autoflow to import a local JSON seed file when a collection is missing.',
  },
] as const;

const questions = [
  {
    question: 'Who is Eldgard for?',
    answer:
      'Developers who work directly with Firestore: debugging a document, investigating a query, reviewing a data change, or reproducing cloud data locally. Eldgard combines that desktop work with a read-only phone companion. It is focused on Firestore, not a general Firebase administration suite for Authentication, Storage, or deployments.',
  },
  {
    question: 'Can I download it yet?',
    answer:
      'Eldgard is currently in development. Public downloads and a release date have not been announced. The planned pricing is shown on this page, but purchases and trials are not open yet. Join the email waitlist to hear when early access opens. Joining does not require a payment or grant immediate access.',
  },
  {
    question: 'Which platforms is it being built for?',
    answer:
      'The desktop app targets macOS, Windows, and Linux. The read-only companion targets iOS and Android. Both are in development; platform-specific release details will be confirmed when public builds are ready.',
  },
  {
    question: 'How will the subscription plans work?',
    answer:
      'Choose $6 USD per developer per month or $60 USD per developer per year. The yearly plan is billed as one $60 payment, equivalent to $5 per month, and saves $12 compared with 12 monthly payments. Both plans include full desktop access, updates, and standard support while subscribed. Your plan renews automatically at the end of each monthly or yearly billing period.',
  },
  {
    question: 'What happens if I cancel my subscription?',
    answer:
      'You can cancel either plan anytime to stop the next renewal. You keep desktop access, updates, and standard support until the end of your paid monthly or yearly billing period; desktop access then ends. Your Firestore data remains in your own projects.',
  },
  {
    question: 'Can a team share one subscription?',
    answer:
      'Either planned subscription covers one named developer, including commercial work, on up to two desktop computers across macOS, Windows, and Linux. Each developer on a team needs their own subscription. You can use as many Firestore projects as you need; Google’s own Firestore usage charges still apply.',
  },
  {
    question: 'Does my database pass through Eldgard’s servers?',
    answer:
      'No. The current apps connect directly from your device to Google APIs or your chosen emulator. Eldgard does not host a copy of your database or use an Eldgard-operated proxy for document access. Saved queries and other app state stay on your device; optional desktop draft recovery and mutation history can retain document content locally. The Privacy Policy explains each storage category.',
  },
  {
    question: 'Will the mobile reader need a paid subscription?',
    answer:
      'The read-only mobile companion for iOS and Android is planned to be free at launch, with no paid desktop plan required. It is also in development; mobile downloads and a release date have not been announced.',
  },
  {
    question: 'Can I take a desktop query to my phone?',
    answer:
      'Yes. Export a saved desktop query for mobile, transfer its text, then import and review it on your phone. Choose your own matching project and database connection, load the draft, and run it when you are ready. This is an explicit handoff: it does not synchronize your workspace, transfer credentials, or automatically execute the query.',
  },
  {
    question: 'How does access to cloud projects work?',
    answer:
      'You can use a Google account with access to the project, or a service-account key. Google sign-in does not grant new permissions: your existing Google Cloud IAM roles determine what you can read or change. Google OAuth refresh tokens are stored in the operating system credential vault.',
  },
  {
    question: 'Is Eldgard made by Google?',
    answer:
      'No. Eldgard is an independent product by alxnsv.io. It is not affiliated with or endorsed by Google or Firebase. Firestore and Firebase are products of Google.',
  },
] as const;

export function nextPreviewIndex(key: string, current: number) {
  if (key === 'ArrowRight') return (current + 1) % previews.length;
  if (key === 'ArrowLeft')
    return (current - 1 + previews.length) % previews.length;
  if (key === 'Home') return 0;
  if (key === 'End') return previews.length - 1;
  return current;
}

function WorkspacePreview() {
  const [active, setActive] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  function handleKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = nextPreviewIndex(event.key, index);
    setActive(next);
    tabs.current[next]?.focus();
  }

  return (
    <div className="eldgard-preview-tour">
      <div className="eldgard-preview-toolbar">
        <div
          className="eldgard-preview-tabs"
          role="tablist"
          aria-label="Explore Eldgard screenshots"
        >
          {previews.map((preview, index) => (
            <button
              key={preview.id}
              type="button"
              role="tab"
              id={`preview-tab-${preview.id}`}
              aria-controls={`preview-panel-${preview.id}`}
              aria-selected={active === index}
              tabIndex={active === index ? 0 : -1}
              ref={(element) => {
                tabs.current[index] = element;
              }}
              onClick={() => setActive(index)}
              onKeyDown={(event) => handleKey(event, index)}
            >
              {preview.label}
            </button>
          ))}
        </div>
        <span className="eldgard-preview-note">Development preview</span>
      </div>
      {previews.map((preview, index) => (
        <div
          key={preview.id}
          id={`preview-panel-${preview.id}`}
          role="tabpanel"
          aria-labelledby={`preview-tab-${preview.id}`}
          tabIndex={0}
          hidden={active !== index}
        >
          <figure className="eldgard-app-preview">
            <img
              src={`/images/eldgard/${preview.image}`}
              width={preview.width}
              height={preview.height}
              alt={preview.alt}
              loading="lazy"
              decoding="async"
            />
            <figcaption>
              <span>{preview.caption}</span>
              <a
                href={`/images/eldgard/${preview.image}`}
                target="_blank"
                rel="noreferrer"
              >
                Full-size preview <ArrowUpRight size={12} aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </figcaption>
          </figure>
        </div>
      ))}
    </div>
  );
}

function ConnectionGuide() {
  const [mode, setMode] = useState<'local' | 'cloud'>('local');
  const local = mode === 'local';

  return (
    <section
      className="eldgard-connections eldgard-section"
      id="connections"
      aria-labelledby="connections-heading"
    >
      <div className="eld-container eldgard-connection-layout">
        <div className="eldgard-connection-copy">
          <p className="eldgard-eyebrow">04 / Connect on your terms</p>
          <h2 id="connections-heading">
            Local roots.
            <br />
            <span>Wider horizons.</span>
          </h2>
          <p>
            Start with your local emulator or connect a Google Cloud project. No
            database migration and no Eldgard-hosted copy of your data.
          </p>
          <fieldset
            className="eldgard-mode-switch"
            aria-label="Connection guide"
          >
            <button
              type="button"
              aria-pressed={local}
              onClick={() => setMode('local')}
            >
              <Terminal size={15} aria-hidden="true" /> Local emulator
            </button>
            <button
              type="button"
              aria-pressed={!local}
              onClick={() => setMode('cloud')}
            >
              <Cloud size={15} aria-hidden="true" /> Google Cloud
            </button>
          </fieldset>
          <p className="eldgard-connection-footnote">
            A guide to the desktop app. This website does not connect to your
            database.
          </p>
        </div>
        <div className="eldgard-guide" aria-live="polite" aria-atomic="true">
          <div className="eldgard-guide-heading">
            <span>
              {local ? (
                <Terminal size={17} aria-hidden="true" />
              ) : (
                <Cloud size={17} aria-hidden="true" />
              )}
              {local ? 'A place to experiment' : 'Your projects, within reach'}
            </span>
            <span>{local ? 'LOCAL' : 'CLOUD'}</span>
          </div>
          <ol>
            <li>
              <span>01</span>
              <div>
                <h3>
                  {local
                    ? 'Start your Firestore emulator.'
                    : 'Sign in with your Google account.'}
                </h3>
                <p>
                  {local
                    ? 'Run the Firebase emulator on your machine.'
                    : 'Choose Google in the app’s Add connection dialog.'}
                </p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <h3>
                  {local
                    ? 'Let Eldgard find it.'
                    : 'Choose the projects you need.'}
                </h3>
                <p>
                  {local
                    ? 'Scan for a running emulator, or enter its host, port, and project ID.'
                    : 'Add projects your account already has permission to access.'}
                </p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <h3>
                  {local
                    ? 'Give your idea some data.'
                    : 'Explore with clear boundaries.'}
                </h3>
                <p>
                  {local
                    ? 'Browse collections, import JSON, and build your first query.'
                    : 'Cloud connections are labeled separately from local emulators.'}
                </p>
              </div>
            </li>
          </ol>
          <div className="eldgard-guide-note">
            {local ? (
              <>
                <Terminal size={14} aria-hidden="true" />
                <code>firebase emulators:start --only firestore</code>
              </>
            ) : (
              <>
                <ShieldCheck size={17} aria-hidden="true" />
                <span>
                  Your existing IAM permissions apply. Sign-in does not grant
                  new access.
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FlameMark({ className = '' }: { className?: string }) {
  return <span className={`eldgard-flame ${className}`} aria-hidden="true" />;
}

function Companion() {
  return (
    <section
      className="eldgard-companion eldgard-section"
      id="companion"
      aria-labelledby="companion-heading"
    >
      <div className="eld-container">
        <div className="eldgard-section-heading">
          <div>
            <p className="eldgard-eyebrow">03 / The mobile companion</p>
            <h2 id="companion-heading">
              Leave the desk.
              <br />
              <span>Keep investigating.</span>
            </h2>
          </div>
          <div className="eldgard-companion-intro">
            <p>
              Check a document without opening your laptop. Eldgard for iOS and
              Android brings focused, read-only Firestore access to your phone,
              using its own cloud or local-network connection.
            </p>
            <p className="eldgard-companion-platforms">
              <Smartphone size={17} aria-hidden="true" /> iOS &amp; Android
              <span>In development</span>
            </p>
          </div>
        </div>

        <p className="eldgard-mobile-capture-note">
          Current app screens · fictional demo data
        </p>
        {/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- Allow keyboard users to scroll the horizontal screenshot gallery. */}
        <section
          className="eldgard-mobile-gallery"
          aria-label="Mobile app screenshots"
          tabIndex={0}
        >
          {mobilePreviews.map((preview) => (
            <figure className="eldgard-companion-visual" key={preview.id}>
              <img
                src={`/images/eldgard/${preview.image}`}
                width={780}
                height={1688}
                alt={preview.alt}
                loading="lazy"
                decoding="async"
              />
              <figcaption>
                <div>
                  <strong>{preview.title}</strong>
                  <span>{preview.caption}</span>
                </div>
                <a
                  href={`/images/eldgard/${preview.image}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${preview.id} screenshot at full size (opens in a new tab)`}
                >
                  Open full-size screen
                  <ArrowUpRight size={13} aria-hidden="true" />
                </a>
              </figcaption>
            </figure>
          ))}
        </section>
        {/* oxlint-enable jsx-a11y/no-noninteractive-tabindex */}

        <div className="eldgard-companion-handoff">
          <div>
            <p className="eldgard-eyebrow">Desktop → mobile</p>
            <h3>Take the query with you.</h3>
            <p>
              Export a saved desktop query and import it on mobile. The query
              travels; your credentials and result documents do not. This is a
              deliberate handoff, not automatic sync.
            </p>
          </div>
          <ol aria-label="Move a saved query from desktop to mobile">
            <li>
              <span aria-hidden="true">01</span>
              <div>
                <h4>Export on desktop</h4>
                <p>Choose Export for mobile on a saved query and copy it.</p>
              </div>
            </li>
            <li>
              <span aria-hidden="true">02</span>
              <div>
                <h4>Review on your phone</h4>
                <p>Import the text and select your matching connection.</p>
              </div>
            </li>
            <li>
              <span aria-hidden="true">03</span>
              <div>
                <h4>Run when you’re ready</h4>
                <p>Load the draft, adjust it, and run or save it yourself.</p>
              </div>
            </li>
          </ol>
        </div>
        <div className="eldgard-companion-notes">
          <p>
            Planned to be free at launch, with no paid desktop plan required.
            Public downloads are not available yet.
          </p>
          <a
            className="eldgard-text-link"
            href="/eldgard/support#support-mobile"
          >
            Companion support <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Runes() {
  return (
    <span className="eldgard-runes" aria-hidden="true">
      ᛁᛚᛐᚴᛆᚱᚦᚱ
    </span>
  );
}

export function EldgardPage() {
  return (
    <div className="eldgard-site">
      <a className="eldgard-skip" href="#content">
        Skip to content
      </a>
      <header className="eldgard-header eld-container">
        <a className="eldgard-brand" href="/eldgard" aria-label="Eldgard home">
          <FlameMark />
          <span>Eldgard</span>
        </a>
        <nav className="eldgard-nav" aria-label="Eldgard navigation">
          <a href="#workspace">Workspace</a>
          <a href="#capabilities">Features</a>
          <a href="#companion">Companion</a>
          <a href="#pricing">Pricing</a>
          <a href="/eldgard/docs">Docs</a>
        </nav>
        <a
          className="eldgard-studio-link"
          href="/"
          aria-label="Back to alxnsv.io"
        >
          <ArrowLeft size={13} aria-hidden="true" /> alxnsv.io
        </a>
      </header>

      <main id="content" tabIndex={-1}>
        <section className="eldgard-hero" aria-labelledby="eldgard-title">
          <div className="eldgard-hero-landscape" aria-hidden="true">
            <img
              src="/images/eldgard/norwegian-fjord.webp"
              alt=""
              width="1915"
              height="821"
              fetchPriority="high"
            />
          </div>
          <div className="eld-container eldgard-hero-inner">
            <div className="eldgard-hero-copy">
              <p className="eldgard-eyebrow">
                <span className="eldgard-status-dot" aria-hidden="true" />
                The Firestore workspace for developers
              </p>
              <h1 id="eldgard-title">
                Your Firestore.
                <br />
                <span>In clear view.</span>
              </h1>
              <p className="eldgard-hero-description">
                Find the document. Understand the query. Review the change.
                Eldgard brings your cloud projects and local emulators into one
                desktop workspace—with a read-only companion for your phone.
              </p>
              <div className="eldgard-actions">
                <a className="eldgard-button" href="#early-access">
                  Request early access{' '}
                  <ArrowRight size={17} aria-hidden="true" />
                </a>
                <a className="eldgard-text-link" href="#workspace">
                  See it in action <ArrowDown size={16} aria-hidden="true" />
                </a>
              </div>
              <p className="eldgard-hero-note">
                Currently in development. Planned desktop pricing: $6/month or
                $60/year.
              </p>
            </div>
            <figure className="eldgard-hero-product">
              <div className="eldgard-hero-screens">
                <img
                  className="eldgard-hero-desktop-screen"
                  src="/images/eldgard/bookings-table.webp"
                  width={2400}
                  height={1500}
                  alt="Eldgard desktop workspace showing a collection of fictional hotel bookings"
                  fetchPriority="high"
                />
                <img
                  className="eldgard-hero-mobile-screen"
                  src="/images/eldgard/mobile-document-fields.png"
                  width={780}
                  height={1688}
                  alt="Current Eldgard mobile reader showing the typed fields of a demo order"
                />
              </div>
              <figcaption>
                Desktop &amp; mobile · Development preview
              </figcaption>
            </figure>
          </div>
          <div className="eld-container eldgard-hero-foot">
            <span>
              <Monitor size={15} aria-hidden="true" />
              Desktop: macOS · Windows · Linux
            </span>
            <a href="#companion">
              <Smartphone size={15} aria-hidden="true" /> iOS &amp; Android
              companion <ArrowDown size={13} aria-hidden="true" />
            </a>
          </div>
        </section>

        <section
          className="eld-container eldgard-workspace eldgard-section"
          id="workspace"
          aria-labelledby="workspace-heading"
        >
          <div className="eldgard-section-heading">
            <div>
              <p className="eldgard-eyebrow">01 / The workspace</p>
              <h2 id="workspace-heading">Real data. A clearer view.</h2>
            </div>
            <p>
              Go from a collection to the field that matters. Keep
              investigations in separate tabs, with table, tree, and JSON views
              for different jobs.
            </p>
          </div>
          <WorkspacePreview />
        </section>

        <section
          className="eld-container eldgard-capabilities eldgard-section"
          id="capabilities"
          aria-labelledby="capabilities-heading"
        >
          <div className="eldgard-section-heading">
            <div>
              <p className="eldgard-eyebrow">02 / Built for the work</p>
              <h2 id="capabilities-heading">
                From “what happened?”
                <br />
                to “here’s the fix.”
              </h2>
            </div>
            <p>
              For developers debugging an app, inspecting a cloud project, or
              setting up the next local test. These workflows are already in the
              development app.
            </p>
          </div>
          <div className="eldgard-capability-grid">
            {capabilities.map(({ icon: Icon, title, text }) => (
              <article className="eldgard-capability" key={title}>
                <div className="eldgard-capability-top">
                  <Icon size={21} strokeWidth={1.5} aria-hidden="true" />
                  <h3>{title}</h3>
                </div>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="eld-container eldgard-principle eldgard-section"
          id="protection"
          aria-labelledby="principle-heading"
        >
          <div className="eldgard-section-heading">
            <div>
              <p className="eldgard-eyebrow">Know what you’re changing</p>
              <h2 id="principle-heading">
                A pause before
                <br />
                <span>the important part.</span>
              </h2>
            </div>
            <p>
              Production data deserves more than an easy-to-click Save button.
              Eldgard gives you context before a single-document change and
              controls for keeping a workspace read-only.
            </p>
          </div>
          <div className="eldgard-protection-grid">
            <article>
              <span className="eldgard-protection-number">01 / BEFORE</span>
              <h3>Lock what you’re inspecting.</h3>
              <p>
                Protect a project or collection with read-only mode. Browsing,
                queries, and exports stay available; new writes from Eldgard are
                blocked. Your Google IAM permissions still apply.
              </p>
            </article>
            <article>
              <span className="eldgard-protection-number">02 / REVIEW</span>
              <h3>See the target and the diff.</h3>
              <p>
                Review the environment, full document path, and typed
                before-and-after values. Single-document edits check the loaded
                server version and reject a stale change instead of overwriting
                it.
              </p>
            </article>
            <article>
              <span className="eldgard-protection-number">03 / RETURN</span>
              <h3>Pick up where you left off.</h3>
              <p>
                Restore tabs and view settings after restarting. Optionally
                retain unfinished document drafts in secure local storage.
                Reconnect and review before applying a recovered edit.
              </p>
            </article>
          </div>
          <div className="eldgard-protection-note">
            <p>
              These controls are not a database backup or a guarantee of bulk
              rollback. Your app connects directly to Google APIs or your chosen
              emulator.
            </p>
            <a
              className="eldgard-text-link"
              href="/eldgard/support#support-protection"
            >
              Read the safeguards <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          </div>
        </section>

        <Companion />

        <ConnectionGuide />

        <EldgardPricing contactHref={contactHref} />

        <section
          className="eld-container eldgard-faq eldgard-section"
          id="questions"
          aria-labelledby="questions-heading"
        >
          <div>
            <p className="eldgard-eyebrow">06 / Before you choose</p>
            <h2 id="questions-heading">
              Your questions.
              <br />
              Clear answers.
            </h2>
            <p>Availability, access, and what your subscription will cover.</p>
          </div>
          <div className="eldgard-questions">
            {questions.map(({ question, answer }) => (
              <details key={question}>
                <summary>
                  {question}
                  <Plus size={16} aria-hidden="true" />
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section
          className="eldgard-access eldgard-section"
          id="early-access"
          aria-labelledby="access-heading"
        >
          <div className="eld-container">
            <Runes />
            <p className="eldgard-eyebrow">Built by Alex Nosov · alxnsv.io</p>
            <h2 id="access-heading">Make room for better Firestore work.</h2>
            <p>
              Be among the first to try Eldgard. Leave your email and we’ll
              let you know when early access opens.
            </p>
            <EldgardWaitlist />
            <p className="eldgard-access-contact">
              Or email <a href="mailto:hello@alxnsv.io">hello@alxnsv.io</a>.
              <br />
              Public downloads and trials are not available yet.
            </p>
          </div>
        </section>
      </main>

      <footer className="eldgard-footer eld-container">
        <a className="eldgard-brand" href="/eldgard">
          <FlameMark />
          <span>Eldgard</span>
        </a>
        <p>© 2026 Eldgard. Independent software, built with care.</p>
        <nav
          className="eldgard-footer-documents"
          aria-label="Eldgard documents"
        >
          <a href="/eldgard/privacy">Privacy</a>
          <a href="/eldgard/terms">Terms</a>
          <a href="/eldgard/support">Support</a>
          <a href="/eldgard/data-deletion">Data &amp; Deletion</a>
        </nav>
        <a href="/">
          Made by alxnsv.io <ArrowRight size={14} aria-hidden="true" />
        </a>
      </footer>
    </div>
  );
}
